import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ProfileLayout,
  UpcomingShowsSection,
  ShowPhotosSection,
  ShowCommentsSection,
  PeopleListSection,
  AboutSection,
} from '../components/Profile';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import type { CommentProps } from '../components/Comment/Comment';
import type { PersonItem } from '../components/Profile';
import type { ShowGroup } from '../components/Profile';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar, ChatIcon, LocationMarkerIcon } from '../components/icons';
import AccentButton from '../components/AccentButton/AccentButton';
import { useHeader } from '../components/Layout/MainLayout';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';

const MOCK_VENUE = {
  name: 'Mercury Lounge',
  address: '1747 S Boston Ave, Tulsa, OK 74119',
  about: 'Johnny Mullenax Band is aksjnsdkn sdkjfn sdkjndskjn sdkjnk jnsd value added value added · autoloremipsum',
};

const MOCK_SHOW_GROUPS: ShowGroup[] = [
  {
    label: 'This afternoon',
    shows: [
      {
        bandName: 'Johnny Mullenax Band',
        date: 'Wed Sep 17',
        venue: 'Mercury Lounge',
        time: '12:00 PM',
        description: 'Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morning.',
        creator: 'Johnny Mullenax',
        creatorType: 'bandMember',
        imageUrl: '/images/bands/johnny-mullenax.png',
      },
      {
        bandName: 'Johnny Mullenax Band',
        date: 'Wed Sep 17',
        venue: 'Mercury Lounge',
        description: 'Live music, tasty brunch, mimosas, and good vibes!',
        creator: 'Johnny Mullenax',
        creatorType: 'bandMember',
        imageUrl: '/images/bands/johnny-mullenax.png',
      },
    ],
  },
  {
    label: 'This evening',
    shows: [
      {
        bandName: 'SOUP',
        date: 'Wed Sep 17',
        venue: 'The Colony',
        time: '8:00 PM',
        description: 'Live music, tasty brunch, mimosas, and good vibes! Enjoy a relaxing Sunday morning, heading left.',
        creator: 'SOUP',
        creatorType: 'bandMember',
      },
    ],
  },
];

const MOCK_PHOTOS = [
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400',
  'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400',
  'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400',
  'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400',
  'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400',
  'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400',
];

const MOCK_COMMENTS: Omit<CommentProps, 'className'>[] = [
  {
    userName: 'Jane Smith',
    timestamp: 'Sept 16 3:29 PM',
    userTags: ['fan', 'showContributor'],
    text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
    showEventInfo: true,
    eventInfo: { artistName: 'Johnny Mullen...', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
  },
  {
    userName: 'George Smith',
    avatarUrl: 'https://i.pravatar.cc/150?img=8',
    timestamp: 'Sept 16 3:29 PM',
    userTags: ['fan'],
    text: 'TEAM DRINK! Welcome back from the tour boys, looking forward to a vegas bomb or three',
    showEventInfo: true,
    eventInfo: { artistName: 'Johnny Mullen...', artistAvatarUrl: '/images/bands/johnny-mullenax.png', date: 'Wed Sep 17', venue: 'Mercury Lounge' },
  },
];

const MOCK_PAST_BANDS: PersonItem[] = [
  { name: 'Johnny Mullenax Band', avatarUrl: '/images/bands/johnny-mullenax.png', avatarShape: 'rounded', route: '/band-profile' },
  { name: 'SOUP', avatarUrl: '/images/bands/soup.png', avatarShape: 'rounded', route: '/band-profile' },
];

const VenueProfile: React.FC = () => {
  const navigate = useNavigate();
  const { setRightExtra } = useHeader();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  const actionItems: ActionSheetItem[] = [
    { key: 'share', label: 'Share', onClick: () => share({ title: MOCK_VENUE.name }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
    { key: 'qr-code', label: 'QR Code', onClick: () => navigate('/venue/mercury/qr') },
  ];

  React.useEffect(() => {
    setRightExtra(
      <AccentButton size="small" shape="round" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MOCK_VENUE.address)}`, '_blank')}>
        Get Directions
      </AccentButton>
    );
    return () => {
      setRightExtra(undefined);
    };
  }, [setRightExtra]);

  return (
    <ProfileLayout
      profileType="venue"
      heroVariant="none"
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
            <LocationMarkerIcon size="xl" tone="lightGrey" />
          </div>
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{MOCK_VENUE.name}</h1>
          <p className="band-identity__subtitle">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MOCK_VENUE.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {MOCK_VENUE.address}
            </a>
          </p>
          <div className="band-identity__badges">
            <UserTag type="venue" onClick={() => setOpenInfoType('venue')} />
          </div>
        </div>
        <UserTypeInfo type="venue" open={openInfoType === 'venue'} onClose={() => setOpenInfoType(null)} />
      </div>

      <UpcomingShowsSection
        showGroups={MOCK_SHOW_GROUPS}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/add-show')}
      />
      <ShowPhotosSection photos={MOCK_PHOTOS} subtitle="Photos from the venue's past shows" />
      <ShowCommentsSection comments={MOCK_COMMENTS} showEventInfo />

      <PeopleListSection
        title="Past Bands"
        subtitle="Bands that have played at Mercury Lounge"
        people={MOCK_PAST_BANDS}
      />

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />
    </ProfileLayout>
  );
};

export default VenueProfile;
